import { DataSource } from "../../Data/DataSource";
import { PartialNullable } from "../../Extensions";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { NAMESPACE } from "./_Namespace";

export type InputListControlPropertyValues =
    InputControlPropertyValues
    & {
        listDataSource: DataSource;
        listDataSourceDescriptionProperty: string;
        listDataSourceValueProperty: string;
    };

export type InputListControlProperties = {
    [name in keyof InputListControlPropertyValues]: Property<InputListControlPropertyValues[name]>
};

export interface IInputListControl extends IInputControl, InputListControlProperties
{
}

export abstract class InputListControl extends InputControl implements IInputListControl
{
    // #region Static

    public static override classNamespace = NAMESPACE + "InputListControl";
    public static override className = "InputListControl";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<InputListControlPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                listDataSourceDescriptionProperty: "description",
                listDataSourceValueProperty: "value",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected __refreshListDataSourceSuspended: boolean;
    protected __refreshingListDataSource: boolean;

    // #endregion

    // #region Properties

    public listDataSource!: Property<DataSource>;
    public listDataSourceDescriptionProperty!: PropertyString;
    public listDataSourceValueProperty!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<InputListControlPropertyValues>)
    {
        super(propertyValues);

        this.__refreshListDataSourceSuspended = false;
        this.__refreshingListDataSource = false;

        this.addCssClass("rad-control-input-list-control");

        // Trigger Property Changes
        this._suspendRefreshListDataSource();
        this.listDataSource.triggerChange();
        this.listDataSourceValueProperty.triggerChange();
        this.listDataSourceDescriptionProperty.triggerChange();
        this._resumeRefreshListDataSource();
    }

    // #endregion

    // #region Public Methods

    public refreshListDataSource()
    {
        if (this.__refreshListDataSourceSuspended)
            return;

        if (this.__refreshingListDataSource)
            return;

        this.__refreshingListDataSource = true;
        this._refreshListDataSource();
        this.__refreshingListDataSource = false;
    }

    // #endregion

    // #region Protected Methods

    protected _listDataSourceChanged(change: PropertyChange<DataSource>)
    {
        this.refreshListDataSource();
    }
    protected _listDataSourceDescriptionPropertyChanged(change: PropertyChange<string>)
    {
        this.refreshListDataSource();
    }
    protected _listDataSourceValuePropertyChanged(change: PropertyChange<string>)
    {
        this.refreshListDataSource();
    }
    protected _refreshListDataSource()
    {
        /* Intentionally empty */
    }
    protected _resumeRefreshListDataSource()
    {
        this.__refreshListDataSourceSuspended = false;
        this.refreshListDataSource();
    }
    protected _suspendRefreshListDataSource()
    {
        this.__refreshListDataSourceSuspended = true;
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: InputListControlPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.listDataSource = new Property<DataSource>({
            context: this,
            dotNetType: "System.Object, System.Private.CoreLib",
            initialValue: propertyValues.listDataSource,
            changeHandler: this._listDataSourceChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "List Data Source" }),
            ],
        });
        this.listDataSourceDescriptionProperty = new PropertyString({
            context: this,
            initialValue: propertyValues.listDataSourceDescriptionProperty,
            changeHandler: this._listDataSourceDescriptionPropertyChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "List Data Source Value Property" }),
            ],
        });
        this.listDataSourceValueProperty = new PropertyString({
            context: this,
            initialValue: propertyValues.listDataSourceValueProperty,
            changeHandler: this._listDataSourceValuePropertyChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "List Data Source Description Property" }),
            ],
        });

        this.listDataSource.overrideSet((value, next) =>
        {
            if (!(value instanceof DataSource))
                value = new DataSource({ data: value });

            next(value);
        });
    }

    // #endregion

    // #endregion
}